import React, { useRef, useState, useEffect } from 'react';

interface Props {
  levelValue: number;
  eloRating?: number;
  topRating?: number;
  myAvatar?: string | null;
  myName?: string;
  topPlayerAvatar?: string | null;
  topPlayerName?: string;
}

export default function LadderView({ levelValue, eloRating, topRating, myAvatar, myName, topPlayerAvatar, topPlayerName }: Props) {
  const elo = eloRating ?? 0;
  const top = Math.max(topRating ?? elo, elo, 1);
  const pct = Math.min((elo / top) * 100, 100);
  const barRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<'mine' | 'top' | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const isTopPlayer = elo >= top && top > 0;

  function handleAvatarHover(e: React.MouseEvent, which: 'mine' | 'top') {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHovered(which);
    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
  }

  function handleAvatarLeave() {
    setHovered(null);
    setTooltipPos(null);
  }

  const tooltipName = hovered === 'mine' ? (myName ?? 'You') : (topPlayerName ?? 'Top Player');
  const tooltipElo = hovered === 'mine' ? elo : top;

  return (
    <div className="ladder" ref={barRef} style={{ '--target-pct': `${pct}%` } as React.CSSProperties}>
      <div className="ladder-fire-header">
        <span className="ladder-category">🔥 ONFIRE RATING</span>
        <h2 className="ladder-league-name">{elo} Punkte</h2>
      </div>

      <div className={`ladder-fire-bar-wrap${visible ? ' ladder-fire-bar-wrap--animate' : ''}`}>
        <div className="ladder-fire-bar">
          <div className="ladder-fire-fill" />
          <div className="ladder-fire-marker ladder-fire-marker--mine">
            {myAvatar
              ? <img src={myAvatar} alt="You" className="ladder-fire-avatar"
                  onMouseEnter={(e) => handleAvatarHover(e, 'mine')}
                  onMouseLeave={handleAvatarLeave}
                />
              : <span className="ladder-fire-marker-dot" />
            }
            <span className="ladder-fire-marker-label">🔥 {elo}</span>
          </div>
          {topPlayerAvatar && !isTopPlayer && (
            <div className="ladder-fire-marker ladder-fire-marker--top">
              <img src={topPlayerAvatar} alt="Top" className="ladder-fire-avatar"
                onMouseEnter={(e) => handleAvatarHover(e, 'top')}
                onMouseLeave={handleAvatarLeave}
              />
            </div>
          )}
        </div>
      </div>

      {hovered && tooltipPos && (
        <div className="ladder-fire-tooltip" style={{ left: tooltipPos.x, top: tooltipPos.y }}>
          <span className="ladder-fire-tooltip-name">{tooltipName}</span>
          <span className="ladder-fire-tooltip-elo">🔥 {tooltipElo}</span>
        </div>
      )}
    </div>
  );
}
